import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimpleIcon, TrashIcon, SignatureIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Self-service entry point (sidebar user card).
export const MySignatureButton = ({ userId }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="my-signature-btn" title="My Signature"
        className="p-1.5 rounded-sm text-zinc-400 hover:text-sky-300 hover:bg-sky-500/10"><SignatureIcon size={16} /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-md" data-testid="my-signature-dialog">
          <DialogHeader><DialogTitle className="font-display">My Signature</DialogTitle></DialogHeader>
          {open && <SignatureManager userId={userId} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

// SIGN-01 · Upload / preview / replace / remove a user's signature (owner or Administrator).
export const SignatureManager = ({ userId }) => {
  const [meta, setMeta] = useState(null);
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await api.get(`/users/${userId}/signature`);
      setMeta(r.data?.active || null);
      setDenied(false);
      if (r.data?.active) {
        const img = await api.get(`/users/${userId}/signature/image`, { responseType: "blob" });
        setSrc(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(img.data); });
      } else setSrc("");
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) { toast.error("Only PNG or JPEG images are accepted"); return; }
    if (f.size > 1024 * 1024) { toast.error("Signature image must be 1 MB or smaller"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/users/${userId}/signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(meta ? "Signature replaced — applies to future authorisations" : "Signature uploaded");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const onRemove = async () => {
    setBusy(true);
    try { await api.delete(`/users/${userId}/signature`); toast.success("Signature removed — issued documents keep theirs"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    setBusy(false);
  };

  if (denied) return <div className="text-xs text-zinc-500" data-testid="signature-no-access">Only the account owner or an Administrator can manage this signature.</div>;
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 space-y-2" data-testid="signature-manager">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400"><SignatureIcon size={14} />Authorised Signature</div>
      <div className="h-20 bg-white rounded-sm flex items-center justify-center overflow-hidden" data-testid="signature-preview">
        {src ? <img src={src} alt="Signature" className="max-h-16 max-w-[220px] object-contain" /> : <span className="text-xs text-zinc-500">No signature uploaded</span>}
      </div>
      {meta && <div className="text-[10px] text-zinc-500 font-mono" data-testid="signature-version">Version {meta.version} · uploaded {String(meta.uploaded_at || "").slice(0, 16).replace("T", " ")}</div>}
      <div className="flex gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid="signature-file-input" onChange={e => onFile(e.target.files?.[0])} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="signature-upload-btn"
          className="h-8 px-3 rounded-sm border border-sky-500/40 text-sky-300 text-xs font-semibold flex items-center gap-1.5 hover:bg-sky-500/10 disabled:opacity-50">
          <UploadSimpleIcon size={13} />{meta ? "Replace Signature" : "Upload Signature"}
        </button>
        {meta && (
          <button type="button" disabled={busy} onClick={onRemove} data-testid="signature-remove-btn"
            className="h-8 px-3 rounded-sm border border-rose-500/40 text-rose-300 text-xs font-semibold flex items-center gap-1.5 hover:bg-rose-500/10 disabled:opacity-50">
            <TrashIcon size={13} />Remove
          </button>
        )}
      </div>
      <div className="text-[10px] text-zinc-500">PNG or JPEG, up to 1 MB. Uploading does not grant authorisation rights.</div>
    </div>
  );
};
